import { platform } from '@/shared/lib/platform/platform'
import { usePdfShareStore } from '@/shared/lib/pdfShareStore'

export type SharePdfResult = 'shared' | 'aborted' | 'unavailable'

const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  const chunkSize = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const slice = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode(...slice)
  }
  return btoa(binary)
}

const triggerWebDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener noreferrer'
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export const pdfBytesToFile = (pdfBytes: Uint8Array, filename: string): File => {
  const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })
  return new File([blob], filename, { type: 'application/pdf', lastModified: Date.now() })
}

export const canSharePdfFile = (file: File): boolean => {
  if (typeof navigator === 'undefined' || typeof navigator.canShare !== 'function') return false
  try {
    return navigator.canShare({ files: [file] })
  } catch {
    return false
  }
}

export const sharePdfFile = async (
  file: File,
  title: string,
  text?: string,
): Promise<SharePdfResult> => {
  if (!canSharePdfFile(file) || typeof navigator.share !== 'function') return 'unavailable'
  try {
    await navigator.share({ files: [file], title, text: text?.trim() || title })
    return 'shared'
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return 'aborted'
    if (err instanceof Error && err.name === 'AbortError') return 'aborted'
    throw err
  }
}

export const downloadPdfFile = (pdfBytes: Uint8Array, filename: string): void => {
  const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })
  triggerWebDownload(blob, filename)
}

/**
 * Salva PDF e abre share sheet no nativo; no web/PWA apresenta o sheet Compartilhar / Baixar
 * (PDF-01) em vez de abrir o visualizador do navegador.
 */
export const savePdfAndShare = async (
  pdfBytes: Uint8Array,
  filename: string,
  shareTitle: string,
  documentId?: string,
): Promise<void> => {
  if (platform.isNative()) {
    const { Filesystem, Directory } = await import('@capacitor/filesystem')
    const { Share } = await import('@capacitor/share')
    const base64 = uint8ArrayToBase64(pdfBytes)
    const { uri } = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Documents,
    })
    await Share.share({ title: shareTitle, url: uri, dialogTitle: shareTitle })
    return
  }

  usePdfShareStore.getState().present({ bytes: pdfBytes, filename, title: shareTitle, documentId })
}
